<template>
  <section class="dc-finding-list" data-testid="finding-list">
    <p v-if="!findings.length" data-testid="finding-empty">{{ $t('damage_control.review.no_findings') }}</p>
    <article v-for="finding in findings" :key="finding.id" class="dc-finding-item" :data-testid="`finding-${finding.id}`">
      <div class="dc-finding-meta">
        <strong>{{ $t(damageTypeKey(finding.damage_type)) }}</strong>
        <span v-if="finding.classification === 'known'">{{ $t('damage_control.review.known_record_label', { id: finding.known_record_id }) }}</span>
        <span v-else-if="finding.classification === 'new'">{{ $t('damage_control.review.classification_new') }}</span>
        <span>{{ $t(finding.decided_via === 'agent' ? 'damage_control.review.source_agent' : 'damage_control.review.source_human') }}</span>
        <span role="status" :data-testid="`finding-status-${finding.id}`">{{ $t(findingStatusKey(finding.status)) }}</span>
      </div>
      <v-alert v-if="finding.stale_known" type="warning" outlined :data-testid="`stale-notice-${finding.id}`">{{ $t('damage_control.review.stale_notice') }}</v-alert>
      <div v-if="editable && finding.status === 'confirmed'" class="dc-finding-actions">
        <template v-if="finding.stale_known">
          <v-btn text :disabled="busyId !== null || refreshing || refreshNeeded" :loading="busyId === finding.id" :data-testid="`resolve-new-${finding.id}`" @click="resolveStale(finding, 'new')">{{ $t('damage_control.review.resolve_new') }}</v-btn>
          <v-btn text :disabled="busyId !== null || refreshing || refreshNeeded" :data-testid="`resolve-reject-${finding.id}`" @click="resolveStale(finding, 'reject')">{{ $t('damage_control.review.resolve_reject') }}</v-btn>
        </template>
        <template v-else>
          <v-btn text :disabled="busyId !== null || refreshing || refreshNeeded" :data-testid="`reject-${finding.id}`" @click="mutate(finding, 'reject')">{{ $t('damage_control.review.reject') }}</v-btn>
          <v-btn v-if="finding.classification !== 'new'" text :disabled="busyId !== null || refreshing || refreshNeeded" :data-testid="`reclassify-new-${finding.id}`" @click="mutate(finding, 'new')">{{ $t('damage_control.review.reclassify_new') }}</v-btn>
          <v-select v-if="finding.classification !== 'known'" :disabled="busyId !== null || refreshing || refreshNeeded" v-model="knownRecordId[finding.id]" :items="zoneEntries" item-text="label" item-value="id" :label="$t('damage_control.review.known_record')" :data-testid="`known-record-${finding.id}`" />
          <v-btn v-if="finding.classification !== 'known'" text :disabled="busyId !== null || refreshing || refreshNeeded || !validKnownId(finding)" :data-testid="`link-known-${finding.id}`" @click="linkKnown(finding)">{{ $t('damage_control.review.link_known') }}</v-btn>
        </template>
      </div>
    </article>
    <v-alert v-if="errorText" type="warning" outlined role="alert" data-testid="finding-error">{{ errorText }}</v-alert>
    <v-btn v-if="refreshNeeded" text :disabled="busyId !== null || refreshing" data-testid="refresh-inspection" @click="refreshInspection">{{ $t('damage_control.capture.refresh') }}</v-btn>
  </section>
</template>

<script>
import { damageControlApi, reportDamageControlFailure } from '@/api/damageControl';
import { damageTypeKey, findingStatusKey } from './presentation';

import { shotStillCurrent, zoneEntries } from "@/utils/damageControlReview";
import reviewRefreshMixin from "@/components/dashboard/damage_control/review/reviewRefreshMixin";

export default {
  name: 'FindingList',
  mixins: [reviewRefreshMixin],
  reviewRefresh: { busyKey: "refreshing", refreshKey: "refreshNeeded", extraBusyKey: "busyId" },
  props: {
    inspection: { type: Object, required: true }, shot: { type: Object, required: true },
    zoneKey: { type: String, required: true }, entries: { type: Array, default: () => [] }, disabled: { type: Boolean, default: false },
  },
  data() { return { busyId: null, knownRecordId: {} }; },
  computed: {
    findings() { return Array.isArray(this.shot.findings) ? this.shot.findings : []; },
    editable() {
      return !this.disabled && this.inspection.status === 'in_progress' && this.shot.current !== false && !this.shot.superseded_by_id && shotStillCurrent(this.inspection, this.zoneKey, this.shot, { coerceId: true });
    },
    zoneEntries() {
      return zoneEntries(this.entries, this.zoneKey, (key, params) => this.$t(key, params));
    },
  },
  methods: {
    refreshInspection() { return this.refresh({ clearBefore: true, retryOnError: true }); },
    damageTypeKey,
    findingStatusKey,
    validKnownId(finding) { return this.zoneEntries.some((entry) => String(entry.id) === String(this.knownRecordId[finding.id])); },
    async mutate(finding, action) {
      if (!this.editable || this.refreshing || this.refreshNeeded || finding.stale_known || !['reject', 'new'].includes(action) || finding.status !== 'confirmed' || this.busyId !== null) return;
      await this.perform(finding, () => action === 'reject' ? damageControlApi.rejectFinding(this.axios, finding.id) : damageControlApi.reclassifyFinding(this.axios, finding.id, { classification: action }));
    },
    async linkKnown(finding) {
      if (!this.editable || this.refreshing || this.refreshNeeded || finding.status !== 'confirmed' || finding.stale_known || this.busyId !== null || !this.validKnownId(finding)) return;
      await this.perform(finding, () => damageControlApi.linkKnownFinding(this.axios, finding.id, this.knownRecordId[finding.id]));
    },
    async resolveStale(finding, resolution) {
      if (!this.editable || this.refreshing || this.refreshNeeded || finding.status !== 'confirmed' || !finding.stale_known || !['new', 'reject'].includes(resolution) || this.busyId !== null) return;
      await this.perform(finding, () => damageControlApi.resolveStaleFinding(this.axios, finding.id, resolution));
    },
    async perform(finding, operation) {
      this.busyId = finding.id; this.errorText = ''; this.refreshNeeded = false;
      try {
        await operation();
        await this.refreshInspection();
      } catch (error) {
        this.errorText = reportDamageControlFailure(this, error, { silent: true }) || this.$t('damage_control.review.update_failed');
      } finally { this.busyId = null; }
    },
  },
};
</script>

<style scoped>
.dc-finding-item { padding: 12px 0; border-bottom: 1px solid rgba(0, 0, 0, .12); }
.dc-finding-meta { display: flex; flex-wrap: wrap; gap: 12px; align-items: baseline; }
.dc-finding-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
@media (max-width: 600px) { .dc-finding-actions > *, .dc-finding-list > .v-btn { width: 100%; } }
</style>
